import { AlertTriangle } from 'lucide-react';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';

export type CardOnFileWarningVariant = 'zero' | 'paid';

interface CardOnFilePermissiveWarningModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  variant?: CardOnFileWarningVariant;
}

const COPY: Record<
  CardOnFileWarningVariant,
  { title: string; paragraphs: string[]; confirm: string }
> = {
  zero: {
    title: 'Allow $0 enrollment without a payment method?',
    paragraphs: [
      'Families will be able to complete Parent Portal enrollment even when no charge is due at enrollment time — for example free classes, scholarships, or sessions that start in the future.',
      'If you collect tuition only outside Enrollio, or you are comfortable without a card on file yet, this can work. Otherwise, future tuition may go unbilled until a card is added.',
    ],
    confirm: 'Yes, allow $0 enroll without a card',
  },
  paid: {
    title: 'Allow paid enrollment without a payment method?',
    paragraphs: [
      'Families will be able to complete Parent Portal enrollment when money is due at checkout without adding a card. Enrollio will not charge at enrollment — the balance stays outstanding (same as staff “skip payment method”).',
      'You may need to collect payment yourself. If no card is on file, later invoice or autopay attempts can fail until a payment method is added.',
    ],
    confirm: 'Yes, allow unpaid enroll when money is due',
  },
};

export function CardOnFilePermissiveWarningModal({
  open,
  onOpenChange,
  onConfirm,
  variant = 'zero',
}: CardOnFilePermissiveWarningModalProps) {
  const copy = COPY[variant];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <div className='flex items-center gap-2'>
            <AlertTriangle className='size-5 text-amber-600' aria-hidden />
            <DialogTitle>{copy.title}</DialogTitle>
          </div>
          <DialogDescription asChild>
            <div className='space-y-3 pt-2 text-left text-sm text-muted-foreground'>
              {copy.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 32)}>{paragraph}</p>
              ))}
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className='gap-2 sm:gap-0'>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type='button'
            variant='default'
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            {copy.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
